import { Trans, useLingui } from '@lingui/react/macro';

import { ActivityOfType } from '@/components/activities/Activity';
import { BaseActivity, BaseActivityText } from '@/components/activities/BaseActivity/BaseActivity';
import IconPaymentValidation from '@/icons/PaymentValidation.svg';

export const PaymentCompletedActivity = ({
  activity,
}: {
  activity: ActivityOfType<'payment_completed'>;
}) => {
  const { t } = useLingui();

  if (activity.transaction?.method === 'point-of-sales') {
    if (activity.transaction.reference) {
      return (
        <BaseActivity
          createdAt={activity.createdAt}
          type="primary"
          icon={<IconPaymentValidation />}
          message={
            <BaseActivityText>
              <Trans id="activities.payment_completed.point-of-sales.with-reference.message">
                Payment completed via cash register (Reference: {activity.transaction.reference}).
              </Trans>
            </BaseActivityText>
          }
        />
      );
    }

    return (
      <BaseActivity
        createdAt={activity.createdAt}
        type="primary"
        icon={<IconPaymentValidation />}
        message={
          <BaseActivityText>
            <Trans id="activities.payment_completed.point-of-sales.message">
              Payment completed via cash register
            </Trans>
          </BaseActivityText>
        }
      />
    );
  } else if (activity.transaction?.method === 'online') {
    return (
      <BaseActivity
        createdAt={activity.createdAt}
        type="primary"
        icon={<IconPaymentValidation />}
        message={
          <BaseActivityText>
            <Trans id="activities.payment_completed.online-method.message">
              <strong>The client</strong> paid the quote
            </Trans>
          </BaseActivityText>
        }
      />
    );
  }

  const creatorName = activity.creator?.name;
  const transactionReference = activity.transaction?.reference;

  let message: React.ReactNode;

  const theRefundOrThePaymentMidSentenceWording =
    activity.transaction?.type === 'client_refund'
      ? t({ id: 'activities.payment_completed.the-refund.mid-sentence', message: 'the refund' })
      : t({ id: 'activities.payment_completed.the-payment.mid-sentence', message: 'the payment' });

  const theRefundOrThePaymentStartSentenceWording =
    activity.transaction?.type === 'client_refund'
      ? t({ id: 'activities.payment_completed.the-refund.start-sentence', message: 'The refund' })
      : t({
          id: 'activities.payment_completed.the-payment.start-sentence',
          message: 'The payment',
        });

  if (creatorName && transactionReference) {
    message = (
      <Trans id="activities.payment_completed.refund.message">
        <strong>{creatorName}</strong> validated {theRefundOrThePaymentMidSentenceWording}. Payment
        reference: {transactionReference}
      </Trans>
    );
  } else if (creatorName && !transactionReference) {
    message = (
      <Trans id="activities.payment_completed.refund.no-payment-reference.message">
        <strong>{creatorName}</strong> validated {theRefundOrThePaymentMidSentenceWording}
      </Trans>
    );
  } else if (!creatorName && transactionReference) {
    message = (
      <Trans id="activities.payment_completed.refund.no-creator.message">
        {theRefundOrThePaymentStartSentenceWording} has been validated. Payment reference:{' '}
        {transactionReference}
      </Trans>
    );
  } else {
    message = (
      <Trans id="activities.payment_completed.refund.no-payment-reference-no-creator.message">
        {theRefundOrThePaymentStartSentenceWording} has been validated
      </Trans>
    );
  }

  return (
    <BaseActivity
      createdAt={activity.createdAt}
      type="primary"
      icon={<IconPaymentValidation />}
      message={<BaseActivityText>{message}</BaseActivityText>}
    />
  );
};
